import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import Scroll from '../components/shared/Scroll';
import { FriendTile } from '../components/FriendTile';
import StateObserver, { user } from '../../StateObserver';
import { ImageView } from '@play-co/timestep-core/ui';
import * as Social from 'src/replicant/social';
import { getLocale } from 'src/replicant/getters/user';
import {
  SocialSubFeature,
  maxSocialContinueRewardMoves,
} from 'src/replicant/social/social.ruleset';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import Checkbox from '../components/shared/Checkbox';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { PinchType } from '../shop/Helpers';
import { SearchInputField } from '../components/SearchInputField';

import { AssetGroup } from '@play-co/timestep-core';

type SocialFriend = {
  id: string;
  name: string;
  photo: string;
};
type CloseHandler = (result: SocialFriend[] | undefined) => void;

const inputStyle = `
  input.popupInviteFriends::placeholder {
    color: #FFFFFF;
  }
`;

/**
 * Scroll list item view
 */
class SocialFriendView extends FlexBoxView {
  private checkbox: Checkbox;
  private title: FriendTile;
  private friend: SocialFriend;

  constructor(
    superview: View,
    opts: {
      getSelected: () => SocialFriend[];
      getFriends: () => Map<SocialFriend, boolean>;
      subFeature: SocialSubFeature;
      friend: SocialFriend;
      updateSelectionCounter: (selectedCount: number) => void;
    },
  ) {
    super({
      superview,
      width: superview.style.width - 10,
      height: uiConfig.buruburu.invite.friendTile.container.height! - 6,
      flex: {
        alignItems: 'center',
      },
    });

    this.friend = opts.friend;

    this.checkbox = new Checkbox({
      superview: this,
      isChecked: opts.getFriends().get(this.friend),
      onChange: (checked) => {
        const selectionCount = opts.getSelected().length;
        const now = StateObserver.now();

        const limit = Social.getters.getInviteLimit(
          user(),
          opts.subFeature,
          now,
        );

        if (checked && selectionCount >= limit) {
          // We've hit the selection limit, so clear the checkbox and return
          this.checkbox.updateState(false);
          return;
        }

        if (checked) {
          sounds.sfx.play('ok');
          opts.getFriends().set(this.friend, true);
          opts.updateSelectionCounter(selectionCount + 1);
        } else {
          sounds.sfx.play('cancel');
          opts.getFriends().set(this.friend, false);
          opts.updateSelectionCounter(selectionCount - 1);
        }
      },
      tracking: {
        feature: 'popup',
        $subFeature: `popup-invitefriends-toggle`,
      },
    });

    getFlexNode(this.checkbox).updateStyle({
      marginLeft: 10,
      marginRight: 6,
    });

    this.title = new FriendTile({
      subFeature: opts.subFeature,
      friend: this.friend,
      onClick: async () => this.checkbox.toggle(),
    });

    this.addSubview(this.title.view);
  }

  public updateFriend(friend: SocialFriend, checked: boolean) {
    this.friend = friend;
    this.checkbox.updateState(checked, true);
    this.title.setFriend(friend);
  }
}

export default class PopupInviteFriends extends PopupBasic {
  private friends!: SocialFriend[];
  private friendsList: Scroll<SocialFriend, SocialFriendView>;
  private buttonCancel: ButtonScaleViewWithText;
  private subFeature!: SocialSubFeature;

  // Used for TEST_FRIEND_SEARCH A/B test
  private style!: HTMLStyleElement;

  private selectedFriends: Map<SocialFriend, boolean> = new Map();
  private selectionCounter: LangBitmapFontTextView;

  constructor(private creationOpts: { superview: View; close: CloseHandler }) {
    super({
      superview: creationOpts.superview,
      close: () => creationOpts.close(undefined),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 915,
      darkerBg: false,
      tracking: { feature: 'popup', $subFeature: 'popup-invitefriends' },
      titleIcon: { image: 'assets/ui/friends/friend_referral_icon.png' },
    });

    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      size: 28,
      width: 440,
      height: 94,
      x: 102,
      y: 74,
      zIndex: 1,
    });

    const friendsListRect = {
      x: 30,
      y: 170,
      width: this.box.style.width - 60,
      height: 600,
    };

    // Friends search feature
    // Attach style element to enable placeholder styling via pseudo-element
    this.style = document.createElement('style');
    this.style.appendChild(document.createTextNode(inputStyle));
    document.head.appendChild(this.style);

    const inputHeight = 64;
    const searchInput = new SearchInputField({
      superview: this.box,
      x: 35,
      y: 200,
      inputClass: 'popupInviteFriends',
      width: friendsListRect.width - 10,
      height: inputHeight,
      placeholder: i18n('social.invite.referral.search'),
      allowClear: true,
    });
    searchInput.onChange = () => this.filterFriends(searchInput.text);

    friendsListRect.y += inputHeight;
    friendsListRect.height -= inputHeight;

    this.friendsList = new Scroll({
      superview: this.box,
      createItem: this.createItem.bind(this),
      setData: this.setData.bind(this),
      showBg: true,
      rect: friendsListRect,
      firstItemOffset: 10,
      recycle: true,
    });

    this.buttonCancel = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: -1,
      fontSize: 46,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: this.box.style.height - 25 - 100 / 2,
      width: 259,
      height: 100,
      centerOnOrigin: true,
      localeText: () => i18n('basic.close'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return this.close([]);
      },
      tracking: { feature: 'popup', $subFeature: 'popup-invitefriends-close' },
    });

    const containerWidth = this.box.style.width;
    this.buttonCancel.updateOpts({ x: containerWidth * 0.265 });

    const { y, width, height } = this.buttonCancel.style;
    const inviteButton = this.box.addSubview(
      new ButtonScaleViewWithText({
        ...uiConfig.buttons.secondary,
        labelOffsetY: -13,
        fontSize: 46,
        font: bitmapFonts('Body'),
        centerOnOrigin: true,
        x: containerWidth * 0.735,
        y,
        width,
        height,
        localeText: () => i18n('social.invite.referral.cta'),
        onClick: async () => {
          const selectedFriends = this.getSelectedFriends();
          if (!selectedFriends.length) {
            return;
          }

          sounds.sfx.play('ok');

          this.close(selectedFriends);
        },
        tracking: {
          feature: 'popup',
          $subFeature: 'popup-invitefriends-invite',
        },
      }),
    );

    this.selectionCounter = inviteButton.addSubview(
      new LangBitmapFontTextView({
        y: inviteButton.style.height / 2,
        width: inviteButton.style.width,
        height: inviteButton.style.height / 2,
        font: 'Body',
        size: 26,
        color: '#017C58',
        align: 'center',
        verticalAlign: 'center',
      }),
    );
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/friends/friend_referral_icon.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/hud/icons/hud_energy.png',
      'assets/ui/match2/common/moves.png',
    ]);
  }

  public init(opts: {
    friends: SocialFriend[];
    subFeature: SocialSubFeature;
    pinch?: PinchType;
  }) {
    this.subFeature = opts.subFeature;
    const pinch = opts.pinch ?? undefined;
    const reward = Social.getters.getInviteReward(user(), opts.subFeature);

    super.init({
      title: this.getTitle(pinch),
      message: this.getRewardMessage(reward.type, reward.value),
    });

    this.friends = [...opts.friends];
    this.friendsList.setItems(
      opts.friends.slice(0, Social.ruleset.inviteFriendListLimit),
    );

    // Add reward icon for message
    this.message.removeAllSubviews();
    this.message.addSubview(this.getRewardIcon(reward.type));

    // update selection counter based on subFeature
    this.updateSelectionCounter(0);
  }

  /**
   * Return title based on runout type
   */
  private getTitle(pinch: PinchType | undefined) {
    switch (pinch) {
      case 'bPoints':
        return i18n('currencyExchange.pinch.runOutOfbPoints');
      case 'energy':
        return i18n('currencyExchange.pinch.runOutOfEnergy');
      case 'moves':
        return i18n('currencyExchange.pinch.runOutOfMoves');
      default:
        return i18n('social.invite.referral.title');
    }
  }

  /**
   * Depends on reward type return different messages for gems anf for energy
   */
  private getRewardMessage(
    type: 'gems' | 'energy' | 'eventEnergy' | 'moves',
    value: number,
  ) {
    switch (type) {
      case 'gems':
        return i18n('social.invite.referral.message.gems', { value });

      case 'energy':
        return i18n('social.invite.referral.message.energy', { value });

      case 'eventEnergy':
        return i18n('social.invite.referral.message.eventEnergy', { value });

      case 'moves':
        return i18n('social.invite.referral.message.moves', {
          max: maxSocialContinueRewardMoves,
        });
    }
  }

  /**
   * Get reward icon for message depend on reward type
   */
  private getRewardIcon(type: 'gems' | 'energy' | 'eventEnergy' | 'moves') {
    switch (type) {
      case 'gems':
        return new ImageView({
          image: 'assets/ui/hud/icons/hud_gem.png',
          x: -70,
          y: 16,
          width: 50,
          height: 42,
          scale: 1.5,
        });
      case 'energy':
        return new ImageView({
          image: 'assets/ui/hud/icons/hud_energy.png',
          x: -70,
          y: 16,
          width: 39,
          height: 46,
          scale: 1.5,
        });
      case 'eventEnergy':
        return new ImageView({
          image: 'assets/ui/hud/icons/hud_energy.png',
          x: -70,
          y: 16,
          width: 39,
          height: 46,
          scale: 1.5,
        });
      case 'moves':
        return new ImageView({
          image: 'assets/ui/match2/common/moves.png',
          x: -70,
          y: 16,
          width: 39,
          height: 43,
          scale: 1.5,
        });
    }
  }

  private close(friends: SocialFriend[]) {
    this.style?.remove();
    this.creationOpts.close(friends);
  }

  /**
   * Create scroll list element
   */
  private createItem(
    superview: View,
    _: any,
    friend: SocialFriend,
  ): SocialFriendView {
    return new SocialFriendView(superview, {
      getSelected: () => this.getSelectedFriends(),
      getFriends: () => this.selectedFriends,
      updateSelectionCounter: this.updateSelectionCounter.bind(this),
      subFeature: this.subFeature,
      friend,
    });
  }

  /**
   * Update scroll list element
   */
  private setData(_: number, friend: SocialFriend, view: SocialFriendView) {
    view.updateFriend(friend, this.selectedFriends.get(friend)!);
  }

  private filterFriends(searchValue: string) {
    let matchedFriends = this.friends;
    if (searchValue) {
      const locale = getLocale(StateObserver.getState().user);
      const upperCaseValue = searchValue.toLocaleUpperCase(locale);
      matchedFriends = matchedFriends.filter(({ name }) =>
        name.toLocaleUpperCase(locale).includes(upperCaseValue),
      );
    }
    this.friendsList.setItems(
      matchedFriends.slice(0, Social.ruleset.inviteFriendListLimit),
    );
  }

  private getSelectedFriends(): SocialFriend[] {
    return Array.from(this.selectedFriends.keys()).filter((friend) =>
      this.selectedFriends.get(friend),
    );
  }

  private updateSelectionCounter(selectedCount: number) {
    if (!this.selectionCounter) {
      return;
    }

    const limit = Social.getters.getInviteLimit(
      user(),
      this.subFeature,
      StateObserver.now(),
    );
    this.selectionCounter.localeText = () => `${selectedCount}/${limit}`;
  }
}
